import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import {
  fetchLeads,
  getLeadShard,
  createLead,
  editLead,
  deleteLead,
  disableLead,
  enableLead,
  updateLeadStatus,
  bulkDeleteLeads,
  bulkUpdateLeadStatus,
} from '@/api/leadsApi'
import { fetchContexts } from '@/api/contextsApi'
import { normalizeContextsList } from '@/utils/contexts'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { unwrapApiList } from '@/utils/graphql'
import {
  normalizeBusinessQueriesList,
  normalizeBusinessQueryRecord,
  businessQueryStatusLabel,
  businessQueryStatusTone,
  buildStatusOptions,
} from '@/utils/businessQueries'
import {
  buildCampaignFilterFieldOptions,
  createEmptyCampaignFilter,
  leadMatchesCampaignFilters,
} from '@/utils/campaignFilters'

const SEARCH_FIELDS = [
  'name',
  'email',
  'phone',
  'message',
  'comments',
  'company',
  'contextName',
  'typeOfBusiness',
  'industry',
  'source',
  'statusLabel',
  'createdOnLabel',
  'requestId',
]

export function getBusinessQueriesErrorMessage(payload) {
  return getApiMessage(payload, 'Failed to load leads')
}

export function useBusinessQueriesData() {
  const store = useStore()
  const route = useRoute()

  const queries = ref([])
  const loading = ref(false)
  const updatingId = ref('')
  const page = ref(1)
  const itemsPerPage = ref(25)
  const statusFilter = ref('')
  // Client-side filter, set when arriving from the upload-history screen.
  const uploadIdFilter = ref(String(route.query?.upload_id || '').trim())
  // Context filter — when set, leads load from the S3 shard cache
  // (getLeadShard) instead of listLeads, per the shard-first pattern.
  const contextFilter = ref(String(route.query?.context_id || '').trim())
  const contexts = ref([])
  const contextsLoading = ref(false)
  /** Local advanced filter rows (field / operator / values). */
  const advancedFilters = ref([])
  const showAdvancedFilters = ref(false)

  const isSessionReady = computed(() => store.getters.isSessionReady)
  const sessionContext = computed(() => store.getters.sessionContext)
  const organization = computed(() => store.state.organization)
  const tableSearch = computed(() => store.state.tableSearch)

  // Each context has its own lead_statuses list — every status picker on
  // this screen derives from the active context, not a hardcoded enum.
  const activeContext = computed(
    () =>
      contexts.value.find(
        (context) => context.context_id === contextFilter.value,
      ) || null,
  )

  const contextStatusOptions = computed(() =>
    buildStatusOptions(activeContext.value?.lead_statuses),
  )

  const statusFilterOptions = computed(() => [
    { value: '', title: 'All statuses' },
    ...contextStatusOptions.value,
  ])

  const advancedFilterFieldOptions = computed(() =>
    buildCampaignFilterFieldOptions(
      activeContext.value ? [activeContext.value] : contexts.value,
    ),
  )

  const activeAdvancedFilterCount = computed(() =>
    advancedFilters.value.filter((row) => {
      const field = String(row?.field ?? '').trim()
      const values = Array.isArray(row?.values) ? row.values : []
      return Boolean(field && values.some((value) => value !== '' && value != null))
    }).length,
  )

  /** Status options for a specific lead's own context. */
  function statusOptionsForQuery(query) {
    const context =
      contexts.value.find(
        (item) =>
          (query?.contextId && item.context_id === query.contextId) ||
          (query?.contextName && item.context_name === query.contextName),
      ) || activeContext.value
    return buildStatusOptions(context?.lead_statuses)
  }

  const filteredQueries = computed(() => {
    let list = queries.value

    const uploadId = uploadIdFilter.value
    if (uploadId) {
      list = list.filter((item) => item.uploadId === uploadId)
    }

    // Shard-cache loads are unfiltered; status filtering happens locally.
    if (statusFilter.value) {
      list = list.filter((item) => item.status === statusFilter.value)
    }

    if (activeAdvancedFilterCount.value) {
      list = list.filter((item) =>
        leadMatchesCampaignFilters(
          item,
          advancedFilters.value,
          advancedFilterFieldOptions.value,
        ),
      )
    }

    const q = (tableSearch.value || '').trim().toLowerCase()
    if (!q) return list

    const fieldKeys = Object.keys(
      activeContext.value?.custom_field_definitions || {},
    )

    return list.filter((item) => {
      if (
        SEARCH_FIELDS.some((field) =>
          String(item[field] ?? '')
            .toLowerCase()
            .includes(q),
        )
      ) {
        return true
      }
      const custom =
        item.customFields && typeof item.customFields === 'object'
          ? item.customFields
          : {}
      const raw = item.raw && typeof item.raw === 'object' ? item.raw : {}
      return fieldKeys.some((key) => {
        const value = custom[key] ?? raw[key] ?? ''
        return String(value).toLowerCase().includes(q)
      })
    })
  })

  const pageCount = computed(() => {
    const perPage = itemsPerPage.value
    if (perPage <= 0) return 1
    return Math.max(1, Math.ceil(filteredQueries.value.length / perPage))
  })

  const paginatedQueries = computed(() => {
    const perPage = itemsPerPage.value
    if (perPage <= 0) return filteredQueries.value
    const start = (page.value - 1) * perPage
    return filteredQueries.value.slice(start, start + perPage)
  })

  const rangeLabel = computed(() => {
    const total = filteredQueries.value.length
    if (!total) return '0–0'
    const perPage = itemsPerPage.value
    if (perPage <= 0) return `1–${total}`
    const start = (page.value - 1) * perPage + 1
    const end = Math.min(page.value * perPage, total)
    return `${start}–${end}`
  })

  const isSearchActive = computed(() => Boolean((tableSearch.value || '').trim()))

  const totalQueries = computed(() => filteredQueries.value.length)

  const statusCounts = computed(() => {
    const counts = {
      PENDING: 0,
      CONTACTED: 0,
      CONVERTED: 0,
      REJECTED: 0,
    }
    queries.value.forEach((item) => {
      if (counts[item.status] != null) counts[item.status] += 1
    })
    return counts
  })

  async function loadContextOptions({ force = false } = {}) {
    const orgId = String(sessionContext.value?.org_id || '').trim()
    if (!orgId || (contexts.value.length && !force)) return

    contextsLoading.value = true
    try {
      const response = await fetchContexts({ org_id: orgId })
      contexts.value = isApiSuccess(response)
        ? normalizeContextsList(unwrapApiList(response))
        : []
    } catch {
      contexts.value = []
    } finally {
      contextsLoading.value = false
    }
  }

  const contextFilterOptions = computed(() =>
    contexts.value.map((context) => ({
      title: context.context_name,
      value: context.context_id,
    })),
  )

  /**
   * Load every shard of a context's S3 lead cache. listContexts'
   * current_lead_shard_index tells us the last valid shard index, so we
   * loop 0..current_lead_shard_index inclusive. getLeadShard returns S3
   * keys; leadsApi downloads each shard and returns { items }.
   */
  async function loadContextShards(context) {
    const contextId = String(context?.context_id ?? '').trim()
    if (!contextId) return []

    const lastShardIndex = Math.max(
      0,
      Number(context?.current_lead_shard_index) || 0,
    )
    const items = []

    for (let index = 0; index <= lastShardIndex; index += 1) {
      const response = await getLeadShard({
        context_id: contextId,
        shard_index: index,
      })
      if (!isApiSuccess(response)) {
        throw new Error(getBusinessQueriesErrorMessage(response))
      }
      const data = response?.data ?? response
      if (Array.isArray(data?.items)) items.push(...data.items)

      // Prefer server-reported total when present (keys-only response).
      const totalShards = Number(data?.total_shards)
      if (Number.isFinite(totalShards) && totalShards > 0 && index + 1 >= totalShards) {
        break
      }
    }

    return items
  }

  /**
   * Shard-first listing: listContexts → getLeadShard per shard index →
   * concatenate. All searching/filtering/sorting happens locally against
   * this in-memory set; listLeads is only a fallback when the org has no
   * contexts at all (e.g. legacy contextless leads).
   */
  async function loadQueries() {
    if (!isSessionReady.value) return { ok: false, skipped: true }

    loading.value = true
    try {
      // Refresh context records so current_lead_shard_index is current.
      await loadContextOptions({ force: true })

      // No hardcoded "all" option — default to the first context. The
      // contextFilter watch skips reloads while loading is true, so this
      // assignment doesn't retrigger loadQueries.
      if (
        contexts.value.length &&
        !contexts.value.some(
          (context) => context.context_id === contextFilter.value,
        )
      ) {
        contextFilter.value = contexts.value[0].context_id
      }

      const targets = contextFilter.value
        ? contexts.value.filter(
            (context) => context.context_id === contextFilter.value,
          )
        : contexts.value

      if (targets.length) {
        const shardLists = await Promise.all(
          targets.map((context) => loadContextShards(context)),
        )
        queries.value = normalizeBusinessQueriesList(shardLists.flat())
      } else {
        const response = await fetchLeads({})
        if (!isApiSuccess(response)) {
          throw new Error(getBusinessQueriesErrorMessage(response))
        }
        queries.value = normalizeBusinessQueriesList(unwrapApiList(response))
      }
      page.value = 1
      return { ok: true }
    } catch (error) {
      queries.value = []
      return {
        ok: false,
        error: getBusinessQueriesErrorMessage(error),
      }
    } finally {
      loading.value = false
    }
  }

  /**
   * Shard sync is best-effort/async and can lag a few seconds, so after our
   * own mutations we patch the in-memory list with the values we just wrote
   * instead of re-fetching.
   */
  function upsertLocalLead(rawLead) {
    const normalized = normalizeBusinessQueryRecord(rawLead)
    const index = queries.value.findIndex(
      (item) => item.requestId && item.requestId === normalized.requestId,
    )
    if (index >= 0) queries.value.splice(index, 1, normalized)
    else queries.value.unshift(normalized)
  }

  function patchLocalLead(requestId, payload) {
    const index = queries.value.findIndex((item) => item.requestId === requestId)
    if (index < 0) return

    const existing = queries.value[index]
    const existingCustom =
      existing.customFields && typeof existing.customFields === 'object'
        ? existing.customFields
        : {}
    const submittedCustom =
      payload.custom_fields && typeof payload.custom_fields === 'object'
        ? payload.custom_fields
        : {}

    const mergedRaw = { ...existing.raw }
    for (const key of [
      'contact_name',
      'contact_email',
      'contact_number',
      'type_of_business',
      'industry',
      'message',
    ]) {
      if (String(payload[key] ?? '').trim()) mergedRaw[key] = payload[key]
    }
    mergedRaw.custom_fields = { ...existingCustom, ...submittedCustom }

    queries.value.splice(index, 1, normalizeBusinessQueryRecord(mergedRaw, index))
  }

  function buildOptimisticLeadRaw(payload, contextName, requestId) {
    const context = contexts.value.find(
      (item) => String(item?.context_name ?? '').trim() === contextName,
    )
    return {
      request_id: requestId,
      context_id: context?.context_id || '',
      context_name: contextName,
      contact_name: payload.contact_name,
      contact_email: payload.contact_email,
      contact_number: payload.contact_number,
      type_of_business: payload.type_of_business,
      industry: payload.industry,
      message: payload.message,
      comments: payload.comments,
      custom_fields: payload.custom_fields || {},
      request_status: 'PENDING',
      is_active: true,
      created_on: new Date().toISOString(),
    }
  }

  async function saveLead({ mode, requestId, form }) {
    const contextNames = (Array.isArray(form?.context_names)
      ? form.context_names
      : [form?.context_names]
    )
      .map((name) => String(name ?? '').trim())
      .filter(Boolean)

    // Edit-only: user explicitly chose "No context" — editLead sends
    // context_name: null to clear the lead's context.
    const clearContext = mode === 'EDIT' && form?.clear_context === true

    if (!contextNames.length && !clearContext) {
      return { ok: false, error: 'Select a product first.' }
    }

    // Fields are dynamic (defined per context); pass through whatever the
    // form collected. org_name is derived server-side and never sent.
    const payload = {
      contact_name: String(form?.contact_name ?? '').trim(),
      contact_email: String(form?.contact_email ?? '').trim(),
      contact_number: String(form?.contact_number ?? '').trim(),
      type_of_business: String(form?.type_of_business ?? '').trim(),
      industry: String(form?.industry ?? '').trim(),
      message: String(form?.message ?? '').trim(),
      comments: String(form?.comments ?? '').trim(),
      custom_fields: form?.custom_fields ?? null,
    }

    if (mode === 'EDIT') {
      const id = String(requestId || '').trim()
      if (!id) return { ok: false, error: 'Lead id is missing.' }

      const editPayload = { ...payload }
      if (Object.prototype.hasOwnProperty.call(form ?? {}, 'recurrence_interval_days')) {
        editPayload.recurrence_interval_days = form.recurrence_interval_days
      }

      const response = await editLead({
        request_id: id,
        // null clears the context; a name moves the lead into that context.
        context_name: clearContext ? null : contextNames[0] || '',
        ...editPayload,
      })
      if (!isApiSuccess(response)) {
        return { ok: false, error: getApiMessage(response, 'Failed to update lead') }
      }

      patchLocalLead(id, payload)
      if (clearContext) {
        const local = queries.value.find((item) => item.requestId === id)
        if (local) {
          local.contextName = ''
          local.contextId = ''
        }
      }
      return {
        ok: true,
        message: getApiMessage(response, 'Lead updated successfully'),
      }
    }

    const response = await createLead({
      ...payload,
      context_names: contextNames,
    })
    if (!isApiSuccess(response)) {
      return { ok: false, error: getApiMessage(response, 'Failed to create lead') }
    }

    // Patch the in-memory list with the ids the server returned — one lead
    // per selected context (request_ids), falling back to request_id.
    const data = response?.data ?? {}
    const returnedIds = Array.isArray(data.request_ids) && data.request_ids.length
      ? data.request_ids
      : [data.request_id].filter(Boolean)
    returnedIds.forEach((newId, index) => {
      const contextName = contextNames[index] ?? contextNames[0] ?? ''
      upsertLocalLead(buildOptimisticLeadRaw(payload, contextName, newId))
    })

    return {
      ok: true,
      message: getApiMessage(response, 'Lead created successfully'),
    }
  }

  async function changeQueryStatus(requestId, requestStatus, comments = '') {
    const id = String(requestId || '').trim()
    const status = String(requestStatus || '').trim().toUpperCase()
    const commentText = String(comments || '').trim()
    if (!id || !status) {
      return { ok: false, error: 'Missing request id or status' }
    }
    if (!commentText) {
      return { ok: false, error: 'Comments are required' }
    }

    updatingId.value = id
    try {
      const response = await updateLeadStatus({
        request_id: id,
        request_status: status,
        comments: commentText,
      })
      if (!isApiSuccess(response)) {
        throw new Error(getApiMessage(response, 'Failed to update status'))
      }

      const local = queries.value.find((item) => item.requestId === id)
      if (local) {
        local.status = status
        local.statusLabel = businessQueryStatusLabel(status)
        local.statusTone = businessQueryStatusTone(status)
        local.comments = commentText
      }

      return {
        ok: true,
        message: getApiMessage(response, 'Status updated successfully'),
      }
    } catch (error) {
      return {
        ok: false,
        error: getApiMessage(error, 'Failed to update status'),
      }
    } finally {
      updatingId.value = ''
    }
  }

  function setLocalLeadActive(requestId, active) {
    const local = queries.value.find((item) => item.requestId === requestId)
    if (local) {
      local.disabled = !active
      local.isActive = active
    }
  }

  async function enableLeadRecord(query) {
    const id = String(query?.requestId || '').trim()
    if (!id) return { ok: false, error: 'Lead id is missing.' }
    const response = await enableLead({ request_id: id })
    if (!isApiSuccess(response)) {
      return { ok: false, error: getApiMessage(response, 'Failed to enable lead') }
    }
    setLocalLeadActive(id, true)
    return { ok: true, message: getApiMessage(response, 'Lead enabled successfully') }
  }

  async function disableLeadRecord(query) {
    const id = String(query?.requestId || '').trim()
    if (!id) return { ok: false, error: 'Lead id is missing.' }
    const response = await disableLead({ request_id: id })
    if (!isApiSuccess(response)) {
      return { ok: false, error: getApiMessage(response, 'Failed to disable lead') }
    }
    setLocalLeadActive(id, false)
    return { ok: true, message: getApiMessage(response, 'Lead disabled successfully') }
  }

  async function deleteLeadRecord(query) {
    const id = String(query?.requestId || '').trim()
    if (!id) return { ok: false, error: 'Lead id is missing.' }
    const response = await deleteLead({ request_id: id })
    if (!isApiSuccess(response)) {
      return { ok: false, error: getApiMessage(response, 'Failed to delete lead') }
    }
    queries.value = queries.value.filter((item) => item.requestId !== id)
    return { ok: true, message: getApiMessage(response, 'Lead deleted successfully') }
  }

  /**
   * Multi-select delete. Partial failures come back per-id in data.errors;
   * the status_message already summarizes "X of Y succeeded".
   */
  async function bulkDeleteLeadRecords(requestIds) {
    const ids = (Array.isArray(requestIds) ? requestIds : [])
      .map((id) => String(id ?? '').trim())
      .filter(Boolean)
    if (!ids.length) return { ok: false, error: 'No leads selected.' }

    const response = await bulkDeleteLeads({ request_ids: ids })
    if (!isApiSuccess(response)) {
      return { ok: false, error: getApiMessage(response, 'Failed to delete leads') }
    }

    // Drop the deleted leads from local state; keep any that failed.
    const errors = Array.isArray(response?.data?.errors)
      ? response.data.errors
      : []
    const failed = new Set(errors.map((item) => String(item?.request_id ?? '')))
    queries.value = queries.value.filter(
      (item) => !ids.includes(item.requestId) || failed.has(item.requestId),
    )

    return {
      ok: true,
      message: getApiMessage(response, 'Selected leads deleted.'),
      errors,
    }
  }

  /** Multi-select status change; comments are mandatory. */
  async function bulkChangeStatus(requestIds, requestStatus, comments) {
    const ids = (Array.isArray(requestIds) ? requestIds : [])
      .map((id) => String(id ?? '').trim())
      .filter(Boolean)
    const status = String(requestStatus || '').trim().toUpperCase()
    const commentText = String(comments || '').trim()
    if (!ids.length) return { ok: false, error: 'No leads selected.' }
    if (!status) return { ok: false, error: 'Select a status.' }
    if (!commentText) return { ok: false, error: 'Comments are required' }

    const response = await bulkUpdateLeadStatus({
      request_ids: ids,
      request_status: status,
      comments: commentText,
    })
    if (!isApiSuccess(response)) {
      return { ok: false, error: getApiMessage(response, 'Failed to update statuses') }
    }

    // Patch the in-memory list optimistically for the leads that changed.
    const failed = new Set(
      (Array.isArray(response?.data?.errors) ? response.data.errors : []).map(
        (item) => String(item?.request_id ?? ''),
      ),
    )
    queries.value.forEach((item) => {
      if (!ids.includes(item.requestId) || failed.has(item.requestId)) return
      item.status = status
      item.statusLabel = businessQueryStatusLabel(status)
      item.statusTone = businessQueryStatusTone(status)
      item.comments = commentText
    })

    return {
      ok: true,
      message: getApiMessage(response, 'Statuses updated.'),
      errors: Array.isArray(response?.data?.errors) ? response.data.errors : [],
    }
  }

  function defaultOrgName() {
    return String(organization.value?.org_name || '').trim()
  }

  function setPage(value) {
    page.value = Number(value) || 1
  }

  function setItemsPerPage(value) {
    itemsPerPage.value = Number(value) || 25
    page.value = 1
  }

  watch(isSessionReady, (ready) => {
    if (ready) void loadQueries()
  }, { immediate: true })

  watch(
    () => route.name,
    (name) => {
      if (name === 'BusinessQueries' && isSessionReady.value) {
        void loadQueries()
      }
    },
  )

  // Status filtering is local against the in-memory shard data — no refetch.
  watch(statusFilter, () => {
    page.value = 1
  })

  watch(
    advancedFilters,
    () => {
      page.value = 1
    },
    { deep: true },
  )

  function addAdvancedFilterRow() {
    if (!advancedFilters.value.length) showAdvancedFilters.value = true
    advancedFilters.value = [
      ...advancedFilters.value,
      createEmptyCampaignFilter(),
    ]
  }

  function removeAdvancedFilterRow(index) {
    advancedFilters.value = advancedFilters.value.filter((_, i) => i !== index)
    if (!advancedFilters.value.length) showAdvancedFilters.value = false
  }

  function clearAdvancedFilters() {
    advancedFilters.value = []
    showAdvancedFilters.value = false
  }

  function toggleAdvancedFilters() {
    showAdvancedFilters.value = !showAdvancedFilters.value
    if (showAdvancedFilters.value && !advancedFilters.value.length) {
      advancedFilters.value = [createEmptyCampaignFilter()]
    }
  }

  watch(contextFilter, () => {
    // The new context may not define the currently-selected status.
    if (
      statusFilter.value &&
      !contextStatusOptions.value.some(
        (option) => option.value === statusFilter.value,
      )
    ) {
      statusFilter.value = ''
    }
    // Skip when loadQueries itself just defaulted the value mid-load.
    if (loading.value) return
    if (isSessionReady.value) void loadQueries()
  })

  watch(
    () => route.query?.context_id,
    (value) => {
      const next = String(value || '').trim()
      if (next && next !== contextFilter.value) contextFilter.value = next
    },
  )

  watch(tableSearch, () => {
    page.value = 1
  })

  watch(
    () => route.query?.upload_id,
    (value) => {
      uploadIdFilter.value = String(value || '').trim()
      page.value = 1
    },
  )

  function clearUploadFilter() {
    uploadIdFilter.value = ''
  }

  return {
    queries,
    loading,
    updatingId,
    page,
    itemsPerPage,
    pageCount,
    statusFilter,
    statusFilterOptions,
    contextStatusOptions,
    statusOptionsForQuery,
    contextFilter,
    contextFilterOptions,
    contextsLoading,
    activeContext,
    uploadIdFilter,
    clearUploadFilter,
    advancedFilters,
    showAdvancedFilters,
    advancedFilterFieldOptions,
    activeAdvancedFilterCount,
    addAdvancedFilterRow,
    removeAdvancedFilterRow,
    clearAdvancedFilters,
    toggleAdvancedFilters,
    isSessionReady,
    filteredQueries,
    paginatedQueries,
    totalQueries,
    statusCounts,
    rangeLabel,
    isSearchActive,
    loadQueries,
    saveLead,
    changeQueryStatus,
    enableLeadRecord,
    disableLeadRecord,
    deleteLeadRecord,
    bulkDeleteLeadRecords,
    bulkChangeStatus,
    defaultOrgName,
    setPage,
    setItemsPerPage,
  }
}
